"use client";

import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";

export interface HowItWorksStep {
  number: string;
  title: string;
  body: string;
}

export interface HowItWorksSectionProps {
  id?: string;
  eyebrow: string;
  title: string;
  steps: HowItWorksStep[];
  accent: string;
  stepAccents: string[];
}

export function HowItWorksSection({
  id = "how-it-works",
  eyebrow,
  title,
  steps,
  accent,
  stepAccents,
}: HowItWorksSectionProps) {
  return (
    <Section id={id} className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-96 w-96 rounded-full opacity-15 blur-[140px]"
          style={{ background: accent }}
        />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny" style={{ color: accent }}>
              {eyebrow}
            </span>
            <h2 className="mt-3">{title}</h2>
          </div>
        </Reveal>

        <div className="mt-12 relative">
          <div
            className="hidden lg:block absolute top-0 bottom-0 left-[27px] w-px"
            style={{
              background: `linear-gradient(180deg, transparent, color-mix(in oklch, ${stepAccents[0] ?? accent} 50%, transparent) 10%, color-mix(in oklch, ${stepAccents[1] ?? accent} 50%, transparent) 30%, color-mix(in oklch, ${stepAccents[2] ?? accent} 50%, transparent) 50%, color-mix(in oklch, ${stepAccents[3] ?? accent} 50%, transparent) 70%, color-mix(in oklch, ${stepAccents[4] ?? accent} 50%, transparent) 90%, transparent)`,
            }}
            aria-hidden
          />

          <div className="space-y-10">
            {steps.map((step, i) => {
              const stepAccent = stepAccents[i % stepAccents.length] ?? accent;
              return (
                <Reveal key={step.number} delay={i * 80}>
                  <div className="relative flex gap-6 items-start">
                    <div className="shrink-0 relative">
                      <div
                        className="grid place-items-center h-14 w-14 rounded-2xl glass relative z-10"
                        style={{
                          boxShadow: `0 0 0 4px rgb(var(--bg)), inset 0 0 0 1px color-mix(in oklch, ${stepAccent} 35%, transparent)`,
                        }}
                      >
                        <span className="text-xs font-bold tracking-wider" style={{ color: stepAccent }}>
                          {step.number}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1 pt-1">
                      <div className="eyebrow-tiny" style={{ color: stepAccent }}>
                        Step {step.number}
                      </div>
                      <h3 className="mt-1">{step.title}</h3>
                      <p className="mt-3 text-body-sm text-[rgb(var(--fg-muted))] leading-relaxed max-w-3xl">
                        {step.body}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </Container>
    </Section>
  );
}
